import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Badge,
  Box,
  Button,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Fade,
  useTheme,
} from '@mui/material';
import { FilterList, LocationOn, Add } from '@mui/icons-material';
import { diveService as apiDiveService } from '../services/api';
import { diveService as accountDiveService } from '../services/diveService';
import { Dive } from '../types/Dive';
import DiveCard from './DiveCard';
import DiveStatistics from './DiveStatistics';
import DiveLocationMap from './DiveLocationMap';
import DiveEntryForm from './DiveEntryForm';
import ShareDivePrompt from './ShareDivePrompt';
import { UnitPreferences, feetToMeters, calculateDiveSAC } from '../utils/unitConversions';
import { OptionsManager, shouldIncludeDive, AppOptions } from '../utils/optionsManager';
import { geocodingService } from '../services/geocodingService';
import { useDiveLocations } from '../hooks/useDiveLocations';
import { userDataService } from '../services/userDataService';
import { preferencesManager } from '../utils/preferencesManager';
import { isUserEditedLocation } from '../utils/diveDisplayName';
import { useDiveSiteCatalog } from '../hooks/useDiveSiteCatalog';
import { useDiveSitePreferences } from '../hooks/useDiveSitePreferences';
import { matchDiveToSite } from '../utils/diveSiteMatcher';
import DiveFiltersPanel, { DiveFilters, countActiveFilters } from './DiveFiltersPanel';
import { parseDurationToMinutes } from '../utils/unitConversions';

// Force recompilation
interface DiveLogTabProps {
  accessToken: string | null;
  onTokenExpired: () => void;
  useAccountDives?: boolean;
}

const DiveLogTab: React.FC<DiveLogTabProps> = ({ accessToken, onTokenExpired, useAccountDives = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [dives, setDives] = useState<Dive[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDiveEntryForm, setShowDiveEntryForm] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [diveFilters, setDiveFilters] = useState<DiveFilters>({
    minDepth: '',
    maxDepth: '',
    minDurationMinutes: '',
    gasData: 'all',
  });
  const [unitPreferences, setUnitPreferences] = useState<UnitPreferences>(() => {
    const optionsManager = OptionsManager.getInstance();
    return optionsManager.getOption('units');
  });
  const [gasConsumptionMetric, setGasConsumptionMetric] = useState<AppOptions['display']['gasConsumptionMetric']>(() => {
    const optionsManager = OptionsManager.getInstance();
    return optionsManager.getOption('display').gasConsumptionMetric;
  });
  const [activityTypeFilters, setActivityTypeFilters] = useState<AppOptions['filters']['activityTypes']>(() => {
    const optionsManager = OptionsManager.getInstance();
    return optionsManager.getOption('filters').activityTypes;
  });
  const [showLocationUpdateNotification, setShowLocationUpdateNotification] = useState(false);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const { locations, locationsVersion, getLocation, saveLocation } = useDiveLocations();
  const { allSites } = useDiveSiteCatalog();
  const { preferences: sitePreferences } = useDiveSitePreferences();
  const processedDivesRef = useRef<string>(''); // Track processed dives by their IDs

  const matchedSitesByDiveId = useMemo(() => {
    const matches = new Map<string, ReturnType<typeof matchDiveToSite>>();

    for (const dive of dives) {
      const matchedSite = matchDiveToSite(dive, allSites, sitePreferences.ignoredDiveSiteMatches[dive.id] ?? []);
      if (!matchedSite) {
        continue;
      }

      matches.set(dive.id, matchedSite);
    }

    return matches;
  }, [dives, allSites, sitePreferences.ignoredDiveSiteMatches]);

  // Wait for preferences to load before geocoding
  useEffect(() => {
    const checkPreferences = () => {
      const prefs = preferencesManager.getPreferences();
      if (prefs && prefs.locationDisplayPreference !== undefined) {
        setPreferencesLoaded(true);
      } else {
      }
    };

    // Check immediately
    checkPreferences();

    // Subscribe to changes
    const unsubscribe = preferencesManager.subscribe(() => {
      checkPreferences();
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    fetchDives();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply saved custom location names from cache first
  useEffect(() => {
    if (dives.length === 0) return;
    if (!preferencesLoaded) return; // Wait for preferences to load

    // Wait a bit for locations to load if they haven't yet
    if (locations.size === 0) {
      return;
    }

    // Create a signature of current dives + locations to prevent re-processing
    const currentSignature = `${dives.map(d => d.id).join(',')}-${locationsVersion}`;
    if (processedDivesRef.current === currentSignature) {
      return; // Already processed this combination
    }

    const locationDisplayPreference = preferencesManager.getPreferences()?.locationDisplayPreference || 'geotagged';

    setDives(prevDives => {
      let updatedCount = 0;
      let skippedCount = 0;
      const updatedDives = prevDives.map(dive => {
        if (!dive.latitude || !dive.longitude) return dive;

        const diveKey = userDataService.generateDiveKey(
          dive.latitude,
          dive.longitude,
          new Date(dive.startTime)
        );

        const savedLocation = getLocation(diveKey, dive.latitude, dive.longitude);
        if (savedLocation && savedLocation.locationName && isUserEditedLocation(savedLocation) && savedLocation.locationName !== dive.location) {
          if (locationDisplayPreference === 'description' && dive.description && dive.description.trim()) {
            skippedCount++;
            return dive;
          }

          updatedCount++;
          return { ...dive, location: savedLocation.locationName };
        }

        return dive;
      });

      if (updatedCount > 0) {
      }
      if (skippedCount > 0) {
      }

      return updatedDives;
    });

    // Mark this combination as processed
    processedDivesRef.current = currentSignature;
  }, [dives, locationsVersion, getLocation, locations.size, preferencesLoaded]); // Re-run when dives load or locations change

  // Subscribe to options changes
  useEffect(() => {
    const optionsManager = OptionsManager.getInstance();
    const unsubscribe = optionsManager.subscribe((options) => {
      setUnitPreferences(options.units);
      setGasConsumptionMetric(options.display.gasConsumptionMetric);
      setActivityTypeFilters(options.filters.activityTypes);
    });
    
    return unsubscribe;
  }, []);

  // Set up automatic background geocoding after initial load
  useEffect(() => {
    if (dives.length === 0) return;
    if (!preferencesLoaded) {
      return;
    }

    const startBackgroundGeocoding = async () => {

      // Check user's location display preference (we know it's loaded now)
      const locationDisplayPreference = preferencesManager.getPreferences()?.locationDisplayPreference || 'geotagged';

      // Get dives that still have coordinate-style locations AND don't have custom saved names
      const divesWithCoordinates = dives.filter(dive => {
        if (!dive.location || !dive.location.includes('°') || !dive.location.includes(',')) return false;
        if (!dive.latitude || !dive.longitude) return false;

        // Skip if user prefers descriptions and this dive has a description
        if (locationDisplayPreference === 'description' && dive.description && dive.description.trim()) {
          return false;
        }

        // Skip if there's a saved custom name for this dive
        const diveKey = userDataService.generateDiveKey(
          dive.latitude,
          dive.longitude,
          new Date(dive.startTime)
        );
        const savedLocation = getLocation(diveKey, dive.latitude, dive.longitude);
        if (savedLocation && savedLocation.locationName && isUserEditedLocation(savedLocation)) {
          return false;
        }

        return true;
      });

      
      if (divesWithCoordinates.length === 0) {
        // Only prefetch if user prefers geotagged locations
        if (locationDisplayPreference === 'geotagged') {
          // Prefetch all dive locations for instant display
          const allLocations = dives
            .filter(d => d.latitude != null && d.longitude != null)
            .map(d => ({ lat: d.latitude!, lng: d.longitude! }));

          if (allLocations.length > 0) {
            geocodingService.prefetchLocations(allLocations);
          }
        } else {
        }
        return;
      }


      // Use batch geocoding for efficiency with multi-threading
      const locationsToGeocode = divesWithCoordinates.map(dive => ({
        lat: dive.latitude!,
        lng: dive.longitude!
      }));

      try {
        const results = await geocodingService.geocodeLocations(locationsToGeocode);
        
        
        // Create a map from coordinates to location names
        const locationMap = new Map<string, string>();
        const divesToSave: Array<{ dive: Dive; locationName: string }> = [];
        
        results.forEach((result, index) => {
          const dive = divesWithCoordinates[index];
          const key = `${dive.latitude!.toFixed(3)},${dive.longitude!.toFixed(3)}`;
          locationMap.set(key, result.name);
          
          // Collect dives that need to be saved to database
          if (result.name) {
            divesToSave.push({ dive, locationName: result.name });
          }
        });
        
        // Save geocoded locations to database/localStorage for persistence
        if (divesToSave.length > 0) {
          
          // Save all locations in parallel (fire and forget - don't block UI updates)
          Promise.all(
            divesToSave.map(({ dive, locationName }) =>
              saveLocation(
                dive.latitude!,
                dive.longitude!,
                new Date(dive.startTime),
                locationName,
                undefined,
                false
              ).catch(err => console.warn(`Failed to save location for dive ${dive.id}:`, err))
            )
          ).then(() => {
          });
        }
        
        // Update all dives at once, but only those without custom saved names
        // AND only if user prefers geotagged locations
        setDives(prevDives => {
          const updatedDives = prevDives.map(prevDive => {
            if (prevDive.latitude && prevDive.longitude) {
              // Check if this dive has a custom saved name
              const diveKey = userDataService.generateDiveKey(
                prevDive.latitude,
                prevDive.longitude,
                new Date(prevDive.startTime)
              );
              const savedLocation = getLocation(diveKey, prevDive.latitude, prevDive.longitude);
              if (savedLocation && savedLocation.locationName && isUserEditedLocation(savedLocation)) {
                // Skip - custom name takes precedence
                return prevDive;
              }

              // IMPORTANT: Don't overwrite location if user prefers descriptions
              // The dive.location field might have coordinates, but getDiveDisplayName
              // will use dive.description based on preference
              const currentPref = preferencesManager.getPreferences()?.locationDisplayPreference || 'geotagged';
              if (currentPref === 'description' && prevDive.description && prevDive.description.trim()) {
                return prevDive;
              }

              const key = `${prevDive.latitude.toFixed(3)},${prevDive.longitude.toFixed(3)}`;
              const newLocation = locationMap.get(key);
              if (newLocation && newLocation !== prevDive.location) {
                return { ...prevDive, location: newLocation };
              }
            }
            return prevDive;
          });
          return updatedDives;
        });
        
        // Show success notification
        setShowLocationUpdateNotification(true);
        setTimeout(() => setShowLocationUpdateNotification(false), 3000);
        
      } catch (error) {
        console.error(`❌ Parallel geocoding failed:`, error);
      }
    };

    // Delay geocoding briefly to ensure saved locations effect runs first
    const timer = setTimeout(startBackgroundGeocoding, 100);

    return () => clearTimeout(timer);
  }, [dives, locationsVersion, getLocation, saveLocation, preferencesLoaded]);

  const fetchDives = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let divesData: Dive[];

      if (useAccountDives) {
        // Load dives from user's account (DynamoDB)
        divesData = await accountDiveService.getMyDives();
      } else {
        // Legacy mode: Load dives directly from Suunto API
        if (!accessToken) {
          return;
        }

        // Initialize demo mode if using demo token
        if (accessToken === 'DEMO_MODE_TOKEN') {
          preferencesManager.initializeDemoMode();
        }

        divesData = await apiDiveService.getDives(accessToken);
      }

      const userId = preferencesManager.getUserId();
      if (userId) {
        const additionalDataByDiveKey = new Map<string, Dive['additionalData']>();

        await Promise.all(
          divesData.map(async (dive) => {
            if (!dive.latitude || !dive.longitude) {
              return;
            }

            const diveKey = userDataService.generateDiveKey(
              dive.latitude,
              dive.longitude,
              new Date(dive.startTime)
            );

            if (additionalDataByDiveKey.has(diveKey)) {
              return;
            }

            const additionalData = await userDataService.getAdditionalDiveData(userId, diveKey);
            if (Object.keys(additionalData).length > 0) {
              additionalDataByDiveKey.set(diveKey, additionalData);
            }
          })
        );

        divesData = divesData.map((dive) => {
          if (!dive.latitude || !dive.longitude) {
            return dive;
          }

          const diveKey = userDataService.generateDiveKey(
            dive.latitude,
            dive.longitude,
            new Date(dive.startTime)
          );

          const storedAdditionalData = additionalDataByDiveKey.get(diveKey);
          if (!storedAdditionalData) {
            return dive;
          }

          return {
            ...dive,
            additionalData: {
              ...dive.additionalData,
              ...storedAdditionalData,
            },
          };
        });
      }

      // Debug: Log first 3 dives to see what we got from API
      divesData.slice(0, 3).forEach((d, i) => {
      });

      setDives(divesData);
      // Reset processed dives ref to force re-application of saved locations
      processedDivesRef.current = '';
    } catch (err: any) {
      console.error('DiveLogTab: Error fetching dives:', err);

      if (useAccountDives) {
        // Account mode errors
        if (err.message?.includes('authentication') || err.message?.includes('Unauthorized')) {
          setError('Your session has expired. Please log in again.');
        } else {
          setError('Failed to fetch dive data. Please try refreshing.');
        }
      } else {
        // Legacy Suunto mode errors
        if (err.response?.status === 401) {
          setError('Your Suunto session has expired. Please reconnect.');
          onTokenExpired();
        } else {
          setError('Failed to fetch dive data. Please try refreshing or reconnecting.');
        }
      }
    } finally {
      setLoading(false);
    }
  }, [accessToken, useAccountDives, onTokenExpired]);


  const handleActivityTypeFilterChange = async (
    activityType: keyof AppOptions['filters']['activityTypes'],
    checked: boolean
  ) => {
    const optionsManager = OptionsManager.getInstance();
    const updatedActivityTypes = {
      ...optionsManager.getOption('filters').activityTypes,
      [activityType]: checked,
    };

    optionsManager.updateCategory('filters', {
      ...optionsManager.getOption('filters'),
      activityTypes: updatedActivityTypes,
    });

    // Persist to the account too. usePreferencesSync already reads
    // customSettings.activityTypeFilters back on login, but nothing wrote it
    // outside the new-user seed, so these filters were localStorage-only and
    // reset to defaults on any other device.
    try {
      if (preferencesManager.getUserId()) {
        await preferencesManager.savePreferences({
          customSettings: { activityTypeFilters: updatedActivityTypes },
        });
      }
    } catch (error) {
      console.error('Failed to save activity type filters to database:', error);
    }
  };

  const handleLocationUpdate = (diveId: string, newLocation: string) => {
    setDives(prevDives =>
      prevDives.map(dive =>
        dive.id === diveId ? { ...dive, location: newLocation } : dive
      )
    );

    // Show success notification
    setShowLocationUpdateNotification(true);
    setTimeout(() => setShowLocationUpdateNotification(false), 3000);
  };

  const [justLoggedDiveId, setJustLoggedDiveId] = useState<string | null>(null);

  const handleDiveEntrySuccess = (createdDiveId?: string) => {
    // Refresh the dive list after successful entry
    fetchDives();
    if (createdDiveId) setJustLoggedDiveId(createdDiveId);
  };

  const sharePrompt = (
    <ShareDivePrompt diveId={justLoggedDiveId} onClose={() => setJustLoggedDiveId(null)} />
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (dives.length === 0) {
    return (
      <Paper
        elevation={1}
        sx={{
          p: 4,
          textAlign: 'center',
          background: isDark
            ? 'linear-gradient(135deg, #1a2733 0%, #1e3347 100%)'
            : 'linear-gradient(135deg, #f0f8ff 0%, #e6f3ff 100%)',
          border: isDark ? '1px solid #42a5f5' : '1px solid #b3d9ff',
        }}
      >
        <Typography variant="h6" color="textSecondary">
          No water activities found
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          {useAccountDives
            ? 'Add your first dive manually or sync from Suunto to get started.'
            : 'Make sure you have water activities (diving, snorkeling, etc.) recorded in your Suunto app.'}
        </Typography>
        <Box display="flex" gap={2} justifyContent="center" mt={2}>
          {useAccountDives && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setShowDiveEntryForm(true)}
            >
              Log Dive
            </Button>
          )}
          <Button variant="outlined" onClick={fetchDives}>
            Refresh
          </Button>
        </Box>
        <DiveEntryForm
          open={showDiveEntryForm}
          onClose={() => setShowDiveEntryForm(false)}
          onSuccess={handleDiveEntrySuccess}
        />
        {sharePrompt}
      </Paper>
    );
  }

  // Filter dives based on activity type preferences, depth, duration, and sort by date (most recent first)
  const filteredDives = dives
    .filter(dive => shouldIncludeDive(dive.diveType, activityTypeFilters))
    .filter(dive => {
      if (diveFilters.minDepth !== '') {
        const minDepthMeters = unitPreferences.depth === 'feet'
          ? feetToMeters(Number(diveFilters.minDepth))
          : Number(diveFilters.minDepth);
        if (dive.maxDepth < minDepthMeters) return false;
      }
      if (diveFilters.maxDepth !== '') {
        const maxDepthMeters = unitPreferences.depth === 'feet'
          ? feetToMeters(Number(diveFilters.maxDepth))
          : Number(diveFilters.maxDepth);
        if (dive.maxDepth > maxDepthMeters) return false;
      }
      if (diveFilters.minDurationMinutes !== '') {
        const durationMins = parseDurationToMinutes(dive.duration);
        if (durationMins < Number(diveFilters.minDurationMinutes)) return false;
      }
      if (diveFilters.gasData !== 'all') {
        // A dive "has data" when an RMV/SAC is derivable from pressures or was entered directly
        const hasGasData = calculateDiveSAC(dive) !== null;
        if (diveFilters.gasData === 'with' && !hasGasData) return false;
        if (diveFilters.gasData === 'without' && hasGasData) return false;
      }
      return true;
    })
    .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

  const activeFilterCount = countActiveFilters(activityTypeFilters, diveFilters);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} mb={3}>
        <Typography variant="h5">Water Activities</Typography>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Fade in={showLocationUpdateNotification}>
            <Chip
              icon={<LocationOn />}
              label="Location updated!"
              color="success"
              size="small"
              sx={{
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                color: '#2E7D2F',
              }}
            />
          </Fade>
          {useAccountDives && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setShowDiveEntryForm(true)}
            >
              Log Dive
            </Button>
          )}
          <Badge badgeContent={activeFilterCount} color="primary">
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={(e) => setFilterAnchorEl(e.currentTarget)}
            >
              Filters
            </Button>
          </Badge>
          <Button variant="outlined" onClick={fetchDives}>
            Refresh
          </Button>
        </Box>
      </Box>

      <DiveFiltersPanel
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        activityTypeFilters={activityTypeFilters}
        onActivityTypeChange={handleActivityTypeFilterChange}
        filters={diveFilters}
        onFiltersChange={setDiveFilters}
        unitPreferences={unitPreferences}
      />

      <DiveStatistics dives={filteredDives} unitPreferences={unitPreferences} gasConsumptionMetric={gasConsumptionMetric} />
      
      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
        Dive Locations
      </Typography>
      
      <DiveLocationMap dives={filteredDives} height={350} />
      
      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
        Recent Activities
        {filteredDives.length !== dives.length && (
          <Chip 
            label={`${filteredDives.length} of ${dives.length} shown`} 
            size="small" 
            color="primary" 
            sx={{ ml: 2 }} 
          />
        )}
      </Typography>
      
      <Grid container spacing={3}>
        {filteredDives.map((dive) => {
          // Get custom location if available
          const diveKey = dive.latitude && dive.longitude
            ? userDataService.generateDiveKey(dive.latitude, dive.longitude, new Date(dive.startTime))
            : undefined;
          const customLocation = diveKey
            ? getLocation(diveKey, dive.latitude, dive.longitude)
            : undefined;

          return (
            <Grid item xs={12} md={6} lg={4} key={dive.id}>
              <DiveCard
                dive={dive}
                unitPreferences={unitPreferences}
                accessToken={accessToken || ''}
                onLocationUpdate={handleLocationUpdate}
                customLocation={customLocation}
                matchedSite={matchedSitesByDiveId.get(dive.id)?.site}
              />
            </Grid>
          );
        })}
      </Grid>

      {filteredDives.length === 0 && dives.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No activities match your current filters. Use the Filters button to adjust activity types, depth range, duration, or gas consumption data.
        </Alert>
      )}

      <DiveEntryForm
        open={showDiveEntryForm}
        onClose={() => setShowDiveEntryForm(false)}
        onSuccess={handleDiveEntrySuccess}
      />
      {sharePrompt}
    </Box>
  );
};

export default DiveLogTab;